import { boardFunctions } from './boardFunctions.js';
import chessPieceCreator, { createChessPiece as createDetailedChessPiece } from './chessPieceCreator.js';
import { getTHREE } from './gameContext.js';
import { highlightSinglePiece, setChessPiecesGroup } from './pieceHighlightManager.js';
import { translatePosition } from './centreBoardMarker.js';
import { setPieceMatrixStatic } from './pieceMatrixState.js';
import { isCellVisibleInCurrentView } from './battle/battleRules.js';


// Add a timer to track when chess pieces were last updated
let lastChessPiecesUpdate = 0;
let baseUpdateInterval = 250; // Base milliseconds between updates
let CHESS_PIECES_UPDATE_INTERVAL = baseUpdateInterval; // Current interval, may increase on errors
let consecutiveErrors = 0; // Track consecutive errors to adjust the update frequency
const MAX_CONSECUTIVE_ERRORS = 5; // After this many errors, log more details
const ERROR_BACKOFF_MULTIPLIER = 2; // Multiply interval by this amount when errors occur
const MAX_UPDATE_INTERVAL = 2000; // Maximum milliseconds between updates

// Error tracking
let lastErrorTime = 0;
let lastErrorMessage = '';
let errorCount = 0;

// Change detection
let lastChessPiecesHash = ''; // Hash of the last processed pieces
let lastUpdateReason = ''; // Reason for the last update
let forcedUpdateCounter = 0; // Force update every N intervals regardless of changes
let lastForcedUpdate = 0; // Timestamp of the last forced update

// Constants for forced update timing
const FORCED_UPDATE_INTERVAL = 60000; // Force an update once per minute (60000ms)
const MOVES_BETWEEN_FORCED_UPDATES = 1; // Force an update after each complete move
let movesSinceLastForcedUpdate = 0; // Counter for moves since last forced update
let lastKnownMoveCount = 0; // Track the last move count to detect new moves

export function updateChessPieces(chessPiecesGroup, camera, gameState) {
	// Rate-limit updates to reduce performance impact (but never skip forced)
	const now = Date.now();
	if (now - lastChessPiecesUpdate < CHESS_PIECES_UPDATE_INTERVAL && !gameState._forceUpdate) {
		return;
	}
	
	// Generate a hash of the current chess pieces + render profile so
	// switching themes triggers a rebuild even when positions haven't
	// changed. The view tag makes entering/leaving a battle arena a
	// "change" too — the visible piece set flips wholesale.
	const profileTag = gameState.renderProfile || (gameState.retroMode ? 'retro' : 'normal');
	const viewTag = gameState.activeBattle?.id || 'world';
	let currentHash = `profile:${profileTag}|view:${viewTag}|`;
	if (gameState.chessPieces && Array.isArray(gameState.chessPieces)) {
		currentHash += gameState.chessPieces.map(piece => {
			if (!piece) return '';
			const pos = piece.position || piece;
			const px = (pos && pos.x !== undefined) ? pos.x : piece.x;
			const pz = (pos && pos.z !== undefined) ? pos.z : piece.z;
			const orientation = Number.isFinite(piece.orientation) ? piece.orientation : '';
			const hasMoved = piece.hasMoved ? 1 : 0;
			const awaiting = piece.awaitingPromotion ? 1 : 0;
			return `${piece.id}-${piece.type}-${piece.player}-${px}-${pz}-${hasMoved}-${orientation}-${awaiting}`;
		}).sort().join('|');
	}
	
	// Check if a move has occurred since last update (if moveCount is available in gameState)
	const currentMoveCount = gameState.moveCount || 0;
	if (currentMoveCount > lastKnownMoveCount) {
		movesSinceLastForcedUpdate += (currentMoveCount - lastKnownMoveCount);
		lastKnownMoveCount = currentMoveCount;
	}
	
	// Force update criteria:
	// 1. After a certain time interval (once per minute)
	// 2. After a complete move has occurred
	// 3. When explicitly requested via _forceUpdate flag
	const timeBasedForceUpdate = (now - lastForcedUpdate >= FORCED_UPDATE_INTERVAL);
	const moveBasedForceUpdate = (movesSinceLastForcedUpdate >= MOVES_BETWEEN_FORCED_UPDATES);
	const explicitForceUpdate = gameState._forceUpdate === true;
	const forcedUpdate = timeBasedForceUpdate || moveBasedForceUpdate || explicitForceUpdate;
	
	// Skip update if the pieces haven't changed and this isn't a forced update
	if (currentHash === lastChessPiecesHash && !forcedUpdate) {
		return;
	}
	
	// Reset force counters on actual updates
	if (forcedUpdate) {
		if (explicitForceUpdate) {
			lastUpdateReason = 'explicit forced update';
		} else if (timeBasedForceUpdate) {
			lastUpdateReason = 'forced minute update';
		} else {
			lastUpdateReason = 'forced move-based update';
		}
		lastForcedUpdate = now;
		movesSinceLastForcedUpdate = 0;
	} else if (currentHash !== lastChessPiecesHash) {
		lastUpdateReason = 'chess pieces changed';
		// Update our hash
		lastChessPiecesHash = currentHash;
	}
	
	// Now we'll proceed with the update since there are changes
	lastChessPiecesUpdate = now;

	// Get THREE safely
	let THREE;
	try {
		THREE = getTHREE();
		// Verify THREE is valid
		if (!THREE) {
			console.error('THREE.js not available in updateChessPieces');
			handleUpdateError(new Error('THREE.js not available'));
			return;
		}
	} catch (err) {
		console.error('Error getting THREE reference:', err);
		handleUpdateError(err);
		return;
	}

	// Safely verify the chess pieces group exists before continuing
	if (!chessPiecesGroup) {
		console.error('Chess pieces group is undefined in updateChessPieces');
		handleUpdateError(new Error('Chess pieces group is undefined'));
		return;
	}
	
	// Set the chessPiecesGroup in the highlight manager
	try {
		setChessPiecesGroup(chessPiecesGroup);
	} catch (err) {
		console.error('Error setting chess pieces group in highlight manager:', err);
		handleUpdateError(err);
	}

	// Only log during debug mode or first run
	const isFirstRun = !chessPiecesGroup?.userData?.initialized;

	// Mark as initialized 
	if (!chessPiecesGroup.userData) {
		chessPiecesGroup.userData = {};
	}
	chessPiecesGroup.userData.initialized = true;

	if (isFirstRun || gameState.debugMode) {
		console.log(`Updating chess pieces visuals (reason: ${lastUpdateReason})`);
	}

	// If we've had too many consecutive errors, reduce update frequency
	if (consecutiveErrors > MAX_CONSECUTIVE_ERRORS) {
		console.warn(`Chess pieces update experiencing ${consecutiveErrors} consecutive errors, throttling updates`);
	}

	try {
		// Safety check - ensure the group has a children array
		if (!chessPiecesGroup.children) {
			console.error('Chess pieces group has no children array');
			chessPiecesGroup.children = [];
			handleUpdateError(new Error('Chess pieces group has no children array'));
			return;
		}

		// Create a map of existing pieces by ID for quick lookup
		const existingPieceMap = {};
		chessPiecesGroup.children.forEach(piece => {
			if (piece && piece.userData && piece.userData.id) {
				existingPieceMap[piece.userData.id] = piece;
			}
		});

		// Extract pieces data - first look in gameState.chessPieces
		let chessPieces = [];

		if (gameState.chessPieces && Array.isArray(gameState.chessPieces) && gameState.chessPieces.length > 0) {
			// Use the pre-extracted pieces from boardFunctions
			chessPieces = gameState.chessPieces;
			if (isFirstRun || gameState.debugMode) {
				console.log(`Using ${chessPieces.length} pre-extracted chess pieces`);
			}
		} else {
			// Otherwise extract chess pieces from board cells using the centralized function
			chessPieces = boardFunctions.extractChessPiecesFromCells(gameState);
			
			// Store them for future use
			gameState.chessPieces = chessPieces;
			
			if (isFirstRun || gameState.debugMode) {
				console.log(`Extracted ${chessPieces.length} chess pieces from board cells using centralized function`);
			}
		}

		// View isolation: while seated in a battle only arena pieces
		// render; outside a battle the remote arena region's pieces are
		// hidden. The full list stays intact on gameState — this only
		// scopes what gets meshes (unprocessed meshes are pruned below).
		if (Array.isArray(chessPieces) && chessPieces.length > 0) {
			chessPieces = chessPieces.filter(piece => {
				const pos = piece?.position;
				if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.z)) return false;
				return isCellVisibleInCurrentView(gameState, pos.x, pos.z);
			});
		}

		// Quick safety check - if we have no chess pieces, stop processing
		if (!chessPieces || chessPieces.length === 0) {
			const existing = [...chessPiecesGroup.children];
			for (const pieceMesh of existing) {
				try {
					chessPiecesGroup.remove(pieceMesh);
					if (pieceMesh.geometry) pieceMesh.geometry.dispose();
					if (pieceMesh.material) {
						if (Array.isArray(pieceMesh.material)) {
							pieceMesh.material.forEach(m => m && m.dispose && m.dispose());
						} else if (pieceMesh.material.dispose) {
							pieceMesh.material.dispose();
						}
					}
				} catch (_) { /* ignore disposal issues */ }
			}
			lastChessPiecesHash = currentHash;
			resetErrorTracking();
			return;
		}

		// Create/update visual pieces
		if (isFirstRun || gameState.debugMode) {
			console.log(`Processing ${chessPieces.length} chess pieces`, chessPieces.slice(0, 2));
		}

		// Keep track of which pieces we've processed to remove any that are no longer needed
		const processedPieceIds = new Set();
		let piecesCreated = 0;
		let piecesReused = 0;

		// Reset the group position to origin - this is critical!
		// The board cells are positioned directly at their coordinates without any group offset
		chessPiecesGroup.position.set(0, 0, 0);

		// Hoist the in-flight optimistic-move pin so the per-piece loop
		// can suppress *position* updates (not just removals) while a
		// tween is running. Without this a heartbeat `game_update`
		// arriving mid-animation snaps the moving mesh back to its old
		// cell, then forward again when the ack lands.
		const PIN_SAFETY_MS = 2000;
		const inFlight = gameState?.inFlightMove;
		const inFlightId = inFlight?.pieceId ? String(inFlight.pieceId) : null;
		const inFlightStillValid = inFlight && (
			!Number.isFinite(inFlight.startedAt)
				|| (now - inFlight.startedAt) < PIN_SAFETY_MS
		);

		chessPieces.forEach(piece => {
			try {
				// Skip invalid pieces
				if (!piece || !piece.position) {
					console.warn('Skipping invalid chess piece:', piece);
					return;
				}

				// Ensure player is always defined and represented consistently
				if (piece.player === undefined || piece.player === null) {
					piece.player = 'unknown';
				}

				// Generate a consistent ID for the piece
				const pieceId = piece.id || `${piece.player}-${piece.type}-${piece.position.x}-${piece.position.z}`;
				processedPieceIds.add(pieceId);

				// Get the position and orientation of the piece
				const { x, z } = piece.position;
				const orientation = piece.orientation || 0;
				
				// Check if we already have this piece at this location
				const existingPiece = existingPieceMap[pieceId];
				let pieceMesh;

				// If the piece exists and hasn't moved, reuse it
				if (existingPiece) {
					// Update color/appearance if needed
					const newColor = getChessPieceColor(piece, gameState);
					const newType = getChessPieceType(piece);
					const existingType = existingPiece.userData ? (existingPiece.userData.pieceType || existingPiece.userData.type) : null;
					const existingColor = existingPiece.userData ? existingPiece.userData.color : null;
					
					// Check if the type or color has changed
					if (existingType !== newType || existingColor !== newColor) {
						// Need to recreate with new appearance
						chessPiecesGroup.remove(existingPiece);
						
						// Create new piece - clear materials for proper garbage collection
						if (existingPiece.material) {
							if (Array.isArray(existingPiece.material)) {
								existingPiece.material.forEach(mat => mat && mat.dispose());
							} else {
								existingPiece.material.dispose();
							}
						}
						
						// Dispose of geometry
						if (existingPiece.geometry) existingPiece.geometry.dispose();

						// Create a new piece with updated appearance
						pieceMesh = createPieceMeshForPlayer(piece, newType, newColor, orientation, gameState, THREE);
						
						pieceMesh.userData = {
							id: pieceId,
							type: 'chessPiece',
							pieceType: newType,
							player: piece.player,
							position: { x, z },
							color: newColor,
							hasMoved: !!piece.hasMoved,
							orientation
						};
						
						// Calculate position relative to centre marker - CRITICAL for alignment
						const absPos = translatePosition({x, z}, gameState, true);
						const adjustX = absPos.x;
						const adjustZ = absPos.z;
						
						pieceMesh.position.set(adjustX, 0.5, adjustZ);
						chessPiecesGroup.add(pieceMesh);
						
						piecesCreated++;
					} else {
						// Just update position if needed
						const absPos = translatePosition({x, z}, gameState, true);
						const adjustX = absPos.x;
						const adjustZ = absPos.z;

						// Don't yank pieces that are currently airborne
						// (wings rule). The wing animation owns their
						// Y position and the surrounding XZ until the
						// settle phase removes the airborne flag.
						const isAirborne = existingPiece.userData && existingPiece.userData.airborne;
						// Don't yank pieces that are mid-tween either.
						// `inFlightMove` covers the optimistic-move
						// window; without this guard a heartbeat
						// `game_update` that arrives mid-animation
						// (server hasn't seen the move yet) snaps the
						// mesh back to its old square, which is the
						// "flick back then forward" the user reported.
						const isPinned = !!(
							inFlight
							&& inFlightId
							&& String(pieceId) === inFlightId
							&& inFlightStillValid
						);
						if (!isAirborne && !isPinned && (
							existingPiece.position.x !== adjustX
							|| existingPiece.position.z !== adjustZ
							|| existingPiece.position.y !== 0.5
						)) {
							existingPiece.position.set(adjustX, 0.5, adjustZ);
						}

						// Continue processing this piece in the shared positioning/userData block below
						pieceMesh = existingPiece;
						piecesReused++;
					}
				} else {
					const pieceColor = getChessPieceColor(piece, gameState);
					const pieceType = getChessPieceType(piece);
					const playerIdentifier = piece.player;
					
					// Try to use direct creation with color
					try {
						pieceMesh = createPieceMeshForPlayer(piece, pieceType, pieceColor, orientation, gameState, THREE);
					} catch (err) {
						console.warn('Error creating piece with color, falling back to getChessPiece:', err);
						// Fall back to regular getChessPiece
						pieceMesh = chessPieceCreator.getChessPiece(pieceType, playerIdentifier);
					}
					
					// Specifically check if this piece is valid with a material before adding it
					if (!pieceMesh) {
						console.error(`Failed to create chess piece: ${pieceType} for player ${piece.player}`);
						return;
					}
					
					// Check if any meshes inside this piece group have materials
					let hasMaterial = false;
					if (pieceMesh.traverse) {
						pieceMesh.traverse(child => {
							if (child && child.isMesh && child.material) {
								hasMaterial = true;
							}
						});
					}

					// If no material found, we need to add a fallback material to at least one mesh
					if (!hasMaterial) {
						console.warn(`Chess piece has no materials: ${pieceType} for player ${piece.player}, adding fallback material`);
						
						// Create a fallback material
						const fallbackMaterial = new THREE.MeshStandardMaterial({
							color: pieceColor,
							roughness: 0.7,
							metalness: 0.3
						});
						
						// Try to add it to the main mesh if there is one
						let materialAdded = false;
						if (pieceMesh.children && pieceMesh.children.length > 0) {
							// Find the first mesh we can add a material to
							pieceMesh.traverse(child => {
								if (!materialAdded && child && child.isMesh) {
									child.material = fallbackMaterial;
									child.material.needsUpdate = true;
									child.visible = true;
									materialAdded = true;
								}
							});
						}
						
						// If we couldn't add to any child, add to the piece itself
						if (!materialAdded) {
							pieceMesh.material = fallbackMaterial;
						}
					}
					
					// Set up piece metadata
					pieceMesh.userData = {
						id: pieceId,
						type: 'chessPiece',
						pieceType: pieceType,
						player: piece.player,
						position: { x, z },
						color: pieceColor,
						hasMoved: !!piece.hasMoved,
						orientation
					};
					
					// Add to the chess pieces group
					chessPiecesGroup.add(pieceMesh);
					piecesCreated++;
				}

				// Position piece correctly relative to the center of the board
				if (pieceMesh) {
					// CRITICAL FIX: Use relative positioning with the center marker
					const absPos = translatePosition({x, z}, gameState, true);
					const adjustX = absPos.x;
					const adjustZ = absPos.z;
					
					pieceMesh.position.x = adjustX;
					pieceMesh.position.z = adjustZ;
					pieceMesh.position.y = 0.5; // Half-height above cell
					
					
					// For orientation 0(facing up): main pieces at bottom, pawns above
					// For orientation 2(facing down): main pieces at top, pawns below
					// For orientation 1(facing right): main pieces at left, pawns to right
					// For orientation 3(facing left): main pieces at right, pawns to left

					// Use Y-axis rotation to rotate pieces horizontally rather than tipping them over
					const orientationVal = Number.isFinite(piece.orientation) ? piece.orientation : 0;
					pieceMesh.rotation.y = orientationVal * Math.PI / 2;

					// Use richer local-piece scale and lighter opponent meshes for speed.
					const localPiece = isLocalPlayerPiece(piece, gameState);
					const renderScale = localPiece ? 1.0 : 0.78;
					if (!pieceMesh.userData.scaleSet || pieceMesh.userData.renderScale !== renderScale) {
						pieceMesh.scale.set(renderScale, renderScale, renderScale);
						pieceMesh.userData.scaleSet = true;
						pieceMesh.userData.renderScale = renderScale;
					}
					
					// Ensure consistent userData for raycasting + move generation
					if (!pieceMesh.userData) pieceMesh.userData = {};
					pieceMesh.userData.id = pieceId;
					pieceMesh.userData.type = 'chessPiece';
					pieceMesh.userData.pieceType = getChessPieceType(piece);
					pieceMesh.userData.player = piece.player;
					pieceMesh.userData.position = { x, z };
					pieceMesh.userData.color = getChessPieceColor(piece, gameState);
					pieceMesh.userData.hasMoved = !!piece.hasMoved;
					pieceMesh.userData.orientation = orientationVal;
					// Mirror per-piece stats so the selected-piece info
					// card can render immediately on click without
					// re-querying gameState.
					pieceMesh.userData.moveCount = Number.isFinite(piece.moveCount) ? piece.moveCount : 0;
					pieceMesh.userData.captureCount = Number.isFinite(piece.captureCount) ? piece.captureCount : 0;
					pieceMesh.userData.distanceTravelled = Number.isFinite(piece.distanceTravelled) ? piece.distanceTravelled : 0;
					pieceMesh.userData.forwardDistance = Number.isFinite(piece.forwardDistance) ? piece.forwardDistance : 0;
					pieceMesh.userData.awaitingPromotion = piece.awaitingPromotion === true;
					applyAwaitingPromotionHalo(pieceMesh, THREE);

					// Add an invisible, larger hitbox so clicks reliably register on pieces (esp. from far camera angles)
					try {
						if (pieceMesh.getObjectByName && !pieceMesh.getObjectByName('raycast-hitbox')) {
							if (!updateChessPieces._raycastHitboxGeometry) {
								// Keep hitbox smaller than a full cell to avoid selecting pieces behind.
								updateChessPieces._raycastHitboxGeometry = new THREE.CylinderGeometry(0.36, 0.36, 0.75, 8);
							}
							if (!updateChessPieces._raycastHitboxMaterial) {
							updateChessPieces._raycastHitboxMaterial = new THREE.MeshBasicMaterial({
								transparent: true,
								opacity: 0,
								depthTest: false,
								depthWrite: false,
								colorWrite: false
							});
							}
							const hitbox = new THREE.Mesh(updateChessPieces._raycastHitboxGeometry, updateChessPieces._raycastHitboxMaterial);
							hitbox.name = 'raycast-hitbox';
							hitbox.position.set(0, 0.38, 0);
							hitbox.castShadow = false;
							hitbox.receiveShadow = false;
							// The hitbox never moves relative to its piece, so
							// freeze its local matrix (static-pieces optimisation).
							setPieceMatrixStatic(hitbox, true);
							pieceMesh.add(hitbox);
						}
					} catch (e) {
						// Non-fatal: hitbox is just for input ergonomics
					}
					
					// Make sure piece is visible
					pieceMesh.visible = true;
					
					// Log positioning for debugging
					// console.log(`Piece positioned at (${adjustX}, ${adjustZ}) relative to centre (${centreX}, ${centreZ})`);
					
					// Ensure materials are properly set and rendered
					if (pieceMesh.material) {
						// Make sure material has basic properties
						if (typeof pieceMesh.material === 'object' && pieceMesh.material !== null) {
							if (typeof pieceMesh.material.needsUpdate !== 'undefined') {
								pieceMesh.material.needsUpdate = true;
							}
							pieceMesh.material.transparent = false;
							pieceMesh.material.opacity = 1.0;
						}
					}
					
					// Enable shadows for better visuals
					pieceMesh.castShadow = true;
					pieceMesh.receiveShadow = true;
					
					// Make sure all child meshes are visible and have proper materials
					if (pieceMesh.traverse && typeof pieceMesh.traverse === 'function') {
						pieceMesh.traverse(child => {
							if (child && child.isMesh) {
								child.visible = true;
								child.castShadow = true;
								child.receiveShadow = true;
								
								if (child.material) {
									// Check if the material is actually an object and not a primitive value
									if (typeof child.material === 'object' && child.material !== null) {
										if (typeof child.material.needsUpdate !== 'undefined') {
											child.material.needsUpdate = true;
										}
										child.material.transparent = false;
										child.material.opacity = 1.0;
									}
								}
							}
						});
					}
					
					// Apply hover highlight if needed
					if (gameState.hoveredPlayer &&
						(String(gameState.hoveredPlayer) === String(piece.player)) &&
						typeof highlightSinglePiece === 'function') {
						try {
							highlightSinglePiece(pieceMesh);
						} catch (highlightErr) {
							console.error('Error highlighting piece:', highlightErr);
						}
					}

					// Static-pieces optimisation: a SETTLED piece doesn't
					// change its transform between syncs, so stop recomputing
					// its local matrix every frame. This runs only when the
					// reconciler fires (rate-limited + hash-gated), and bakes
					// the final transform set above. Pieces that are mid-
					// animation must stay dynamic — the wing animation owns an
					// airborne piece's per-frame Y, and the optimistic-move pin
					// owns the moving mesh — so we keep those flagged dynamic;
					// their animation owners re-freeze them when they settle.
					const pieceAnimating =
						!!(pieceMesh.userData && (pieceMesh.userData.airborne || pieceMesh.userData.inFlight))
						|| !!(inFlight && inFlightId && String(pieceId) === inFlightId && inFlightStillValid);
					setPieceMatrixStatic(pieceMesh, !pieceAnimating);
				}
			} catch (pieceErr) {
				console.error('Error processing chess piece:', pieceErr);
			}
		});

		// Remove any pieces that are no longer in the game. Honour the
		// in-flight optimistic move pin (hoisted earlier) so a
		// `game_update` arriving mid-animation can't yank the moving
		// mesh out from underneath the tween.
		// One mesh per board cell — drop stale ghosts (e.g. captured pawn
		// under the capturing knight) that survived a partial sync.
		const cellToCanonicalId = new Map();
		for (const piece of chessPieces) {
			if (!piece?.position) continue;
			const key = `${piece.position.x},${piece.position.z}`;
			const pid = piece.id || `${piece.player}-${piece.type}-${piece.position.x}-${piece.position.z}`;
			cellToCanonicalId.set(key, String(pid));
		}

		const currentPieces = [...chessPiecesGroup.children];
		let piecesRemoved = 0;

		currentPieces.forEach(pieceMesh => {
			try {
				if (pieceMesh && pieceMesh.userData && pieceMesh.userData.id) {
					if (!processedPieceIds.has(pieceMesh.userData.id)) {
						if (inFlightStillValid
							&& inFlightId
							&& String(pieceMesh.userData.id) === inFlightId
						) {
							// Pinned — keep mesh, let the move flow drop or
							// adopt it once the ack arrives.
							return;
						}
						// Airborne pieces own their own removal animation
						// (wings flap, then fall into the water). Skip
						// the standard prune so we don't snip the mesh
						// mid-fall.
						if (pieceMesh.userData.airborne) {
							return;
						}
						chessPiecesGroup.remove(pieceMesh);
						if (pieceMesh.geometry) pieceMesh.geometry.dispose();
						if (pieceMesh.material) {
							if (Array.isArray(pieceMesh.material)) {
								pieceMesh.material.forEach(m => m.dispose());
							} else {
								pieceMesh.material.dispose();
							}
						}
						piecesRemoved++;
					}
				} else {
					chessPiecesGroup.remove(pieceMesh);
					piecesRemoved++;
				}
			} catch (removeErr) {
				console.error('Error removing chess piece:', removeErr);
			}
		});

		for (const pieceMesh of [...chessPiecesGroup.children]) {
			try {
				const pos = pieceMesh?.userData?.position;
				const meshId = pieceMesh?.userData?.id;
				if (!pos || !meshId) continue;
				const key = `${pos.x},${pos.z}`;
				const canonicalId = cellToCanonicalId.get(key);
				if (!canonicalId) continue;
				if (String(meshId) === canonicalId) continue;
				if (inFlightStillValid && inFlightId && String(meshId) === inFlightId) continue;
				if (pieceMesh.userData.airborne) continue;
				chessPiecesGroup.remove(pieceMesh);
				if (pieceMesh.geometry) pieceMesh.geometry.dispose();
				if (pieceMesh.material) {
					if (Array.isArray(pieceMesh.material)) {
						pieceMesh.material.forEach(m => m && m.dispose && m.dispose());
					} else if (pieceMesh.material.dispose) {
						pieceMesh.material.dispose();
					}
				}
				piecesRemoved++;
			} catch (dedupeErr) {
				console.error('Error deduping chess piece at cell:', dedupeErr);
			}
		}

		// If we're here due to a forced update but nothing actually changed,
		// we can skip the verbose logging at the end
		const skipDetailedLogging = forcedUpdate && piecesCreated === 0 && piecesRemoved === 0;

		// if ((isFirstRun || gameState.debugMode || piecesCreated > 0 || piecesRemoved > 0) 
		// 	&& !skipDetailedLogging) {
		// 	console.log(`Chess pieces updated: ${piecesCreated} created, ${piecesReused} reused, ${piecesRemoved} removed`);
		// }
		
		lastChessPiecesHash = currentHash;

		// If we get here without errors, reset error tracking
		resetErrorTracking();
	} catch (error) {
		console.error('Error in updateChessPieces:', error);
		handleUpdateError(error);
	}
}

/**
 * Handle update errors and adjust update frequency
 * @param {Error} error - The error that occurred
 */
function handleUpdateError(error) {
	const now = Date.now();
	
	// Track error types
	const errorMsg = error.message || 'Unknown error';
	
	// Check if this is the same error as before
	if (errorMsg === lastErrorMessage) {
		errorCount++;
	} else {
		// New error type
		lastErrorMessage = errorMsg;
		errorCount = 1;
	}
	
	// Increment consecutive errors
	consecutiveErrors++;
	
	// Only log full details periodically to avoid console spam
	if (now - lastErrorTime > 5000 || consecutiveErrors % 10 === 0) {
		console.error(`Chess pieces update error (${consecutiveErrors} consecutive, ${errorCount} of this type): ${errorMsg}`);
		lastErrorTime = now;
	}
	
	// Increase the update interval to reduce error frequency
	if (consecutiveErrors > 3) {
		CHESS_PIECES_UPDATE_INTERVAL = Math.min(
			CHESS_PIECES_UPDATE_INTERVAL * ERROR_BACKOFF_MULTIPLIER,
			MAX_UPDATE_INTERVAL
		);
		console.warn(`Increasing chess pieces update interval to ${CHESS_PIECES_UPDATE_INTERVAL}ms due to errors`);
	}
}

/**
 * Reset error tracking when updates succeed
 */
function resetErrorTracking() {
	if (consecutiveErrors > 0) {
		consecutiveErrors = 0;
		// Gradually reduce the update interval back to base level
		if (CHESS_PIECES_UPDATE_INTERVAL > baseUpdateInterval) {
			CHESS_PIECES_UPDATE_INTERVAL = Math.max(
				baseUpdateInterval,
				CHESS_PIECES_UPDATE_INTERVAL / ERROR_BACKOFF_MULTIPLIER
			);
		}
	}
}

function getResolvedLocalPlayerId(gameState) {
	if (!gameState) return null;
	return gameState.localPlayerId || gameState.myPlayerId || null;
}

function isLocalPlayerPiece(piece, gameState) {
	const localPlayerId = getResolvedLocalPlayerId(gameState);
	if (!localPlayerId || !piece) return false;
	return String(piece.player) === String(localPlayerId);
}

const RETRO_CHARS = { KING: '\u041A', QUEEN: '\u0424', ROOK: '\u041B', BISHOP: '\u0421', KNIGHT: '\u041A\u043D', PAWN: '\u041F' };

function createRetroPiece(type, isLocal, THREE) {
	const c = document.createElement('canvas');
	c.width = 128; c.height = 128;
	const x = c.getContext('2d');
	x.clearRect(0, 0, 128, 128);
	const clr = isLocal ? '#00ff41' : '#ff8800';
	x.shadowColor = clr; x.shadowBlur = 12;
	x.fillStyle = clr;
	const ch = RETRO_CHARS[type] || '?';
	x.font = `bold ${ch.length > 1 ? 56 : 80}px monospace`;
	x.textAlign = 'center'; x.textBaseline = 'middle';
	x.fillText(ch, 64, 64);
	const tex = new THREE.CanvasTexture(c);
	tex.needsUpdate = true;
	const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
	const spr = new THREE.Sprite(mat);
	spr.scale.set(0.9, 0.9, 0.9);
	spr.position.y = 0.6;
	const g = new THREE.Group();
	g.add(spr);
	return g;
}

function createPieceMeshForPlayer(piece, pieceType, pieceColor, orientation, gameState, THREE) {
	const piecePos = piece?.position || piece;
	const isLocalPiece = isLocalPlayerPiece(piece, gameState);

	// Always go through the detailed creator so every player's pieces
	// share the same scale and silhouette (only the trim detail
	// differs). The previous opponent-only `simplePieces` fallback was
	// ~50% larger because its geometries used a different radius —
	// the user observed "their lighter red pieces are bigger than
	// mine" because of this mismatch.
	if (
		typeof createDetailedChessPiece === 'function' &&
		piecePos &&
		Number.isFinite(piecePos.x) &&
		Number.isFinite(piecePos.z)
	) {
		try {
			return createDetailedChessPiece(
				gameState,
				piecePos.x,
				piecePos.z,
				pieceType,
				piece.player,
				{
					orientation,
					isLocalPlayer: isLocalPiece,
					color: pieceColor,
				}
			);
		} catch (error) {
			console.warn('Detailed piece creation failed, using fallback:', error);
		}
	}

	return chessPieceCreator.createPiece(pieceType, pieceColor, orientation, THREE);
}

/**
 * Convert any colour representation (number, `#rrggbb`, or
 * `rgb(r,g,b)` / `hsl(...)`) to a numeric hex value that
 * `THREE.Color.setHex` and friends will accept.
 */
function toHexNumber(colour) {
	if (typeof colour === 'number') return colour;
	if (typeof colour !== 'string' || colour.length === 0) return null;
	if (colour.startsWith('#')) {
		const hex = colour.length === 4
			// `#abc` → `#aabbcc`
			? colour[1] + colour[1] + colour[2] + colour[2] + colour[3] + colour[3]
			: colour.slice(1);
		const parsed = parseInt(hex, 16);
		return Number.isFinite(parsed) ? parsed : null;
	}
	// Defer rgb()/hsl()/named colours to THREE.Color, which knows the
	// CSS color set. We can't use it directly here without a THREE
	// reference, but the parsed integer is what most callers need.
	return null;
}

// Animated yellow halo that hovers around frozen pawns. Shared geometry
// + material across all halos; per-mesh user data stores the animation
// start so each pulses independently.
const HALO_NAME = 'awaiting-promotion-halo';

function applyAwaitingPromotionHalo(pieceMesh, THREE) {
	if (!pieceMesh || !pieceMesh.getObjectByName) return;
	const existing = pieceMesh.getObjectByName(HALO_NAME);
	if (!pieceMesh.userData?.awaitingPromotion) {
		if (existing) {
			pieceMesh.remove(existing);
			if (existing.geometry) existing.geometry.dispose();
			if (existing.material && existing.material.dispose) existing.material.dispose();
		}
		return;
	}
	if (existing) return;

	if (!applyAwaitingPromotionHalo._geometry) {
		applyAwaitingPromotionHalo._geometry = new THREE.RingGeometry(0.46, 0.6, 32);
	}
	const material = new THREE.MeshBasicMaterial({
		color: 0xffd54a,
		transparent: true,
		opacity: 0.55,
		side: THREE.DoubleSide,
		depthTest: false,
		depthWrite: false,
	});
	const halo = new THREE.Mesh(applyAwaitingPromotionHalo._geometry, material);
	halo.name = HALO_NAME;
	halo.rotation.x = -Math.PI / 2;
	halo.position.set(0, 0.05, 0);
	halo.renderOrder = 999;
	halo.userData.bornAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
	pieceMesh.add(halo);
}

/**
 * Tick every visible halo so it pulses. Called from the main render
 * loop; cheap when nobody has a frozen pawn (loop exits immediately).
 *
 * @param {THREE.Group} chessPiecesGroup
 */
export function updateAwaitingPromotionHalos(chessPiecesGroup) {
	if (!chessPiecesGroup || !Array.isArray(chessPiecesGroup.children)) return;
	const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
	for (const pieceMesh of chessPiecesGroup.children) {
		const halo = pieceMesh && pieceMesh.getObjectByName && pieceMesh.getObjectByName(HALO_NAME);
		if (!halo) continue;
		const t = (now - (halo.userData.bornAt || now)) / 1000;
		const pulse = 0.4 + 0.25 * Math.sin(t * 3.2);
		if (halo.material) halo.material.opacity = pulse;
		const scale = 1 + 0.08 * Math.sin(t * 3.2);
		halo.scale.set(scale, scale, 1);
		halo.rotation.z = t * 0.6;
	}
}

/**
 * Get the color for a chess piece
 * @param {Object} piece - The chess piece data
 * @param {Object} gameState - The current game state
 * @returns {number} - The color as a hexadecimal number
 */
function getChessPieceColor(piece, gameState) {
	// If piece has an explicit color, use it (converted to a hex
	// number — server stores colours as strings like '#a1b2c3').
	if (piece.color && piece.color !== 0xcccccc) {
		const asNumber = toHexNumber(piece.color);
		if (asNumber !== null) return asNumber;
	}
	
	// Use the centralized color function for consistent colors
	if (boardFunctions && typeof boardFunctions.getPlayerColor === 'function') {
		return boardFunctions.getPlayerColor(piece.player, gameState, 'chess');
	}
	
	// Fallback: highlight only the local human's pieces (not whoever's
	// tetris turn is active — that made opponents look "yours").
	const localId = gameState && (gameState.localPlayerId || gameState.myPlayerId);
	if (localId && String(piece.player) === String(localId)) {
		return 0xAA0000;
	}
	
	// Player 1 is usually white
	if (String(piece.player) === '1') {
		return 0xf0f0f0; // White
	} 
	// Player 2 is usually black
	else if (String(piece.player) === '2') {
		return 0x222222; // Black
	}
	
	// Convert player ID to a deterministic color
	const playerId = String(piece.player);
	// Simple hash function
	let hash = 0;
	for (let i = 0; i < playerId.length; i++) {
		hash = playerId.charCodeAt(i) + ((hash << 5) - hash);
	}
	// Use the hash to generate RGB values, but ensure blue/green dominance
	const r = Math.min(80, (hash & 0xFF0000) >> 16); // Keep red low
	const g = 100 + Math.min(155, (hash & 0x00FF00) >> 8); // Medium to high green
	const b = 150 + Math.min(105, hash & 0x0000FF); // Medium to high blue
	return (r << 16) | (g << 8) | b;
}

/**
 * Get the type of chess piece with consistent naming
 * @param {Object} piece - The chess piece data
 * @returns {string} - The standardized piece type
 */
function getChessPieceType(piece) {
	// Normalize piece type to uppercase
	const pieceType = (piece.type || 'PAWN').toUpperCase();
	
	// Map any variant names to standard names
	const typeMap = {
		'P': 'PAWN',
		'R': 'ROOK',
		'N': 'KNIGHT',
		'B': 'BISHOP',
		'Q': 'QUEEN',
		'K': 'KING'
	};
	
	return typeMap[pieceType] || pieceType;
}

